import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import CuadreMensual from '../components/vendedores/CuadreMensual';

const fmt = v => new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',minimumFractionDigits:0}).format(v||0);
const hoy = () => new Date().toISOString().slice(0,10);
const primerDiaMes = () => { const d=new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0,10); };

export default function Vendedores() {
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol === 'admin' || usuario?.rol === 'superadmin';

  const [vendedores, setVendedores] = useState([]);
  const [config, setConfig] = useState({ pct_empresa:70, pct_equipo:25, pct_vendedor:5 });
  const [reporte, setReporte] = useState(null);
  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(hoy());
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({ nombre:'', documento:'', telefono:'', email:'' });
  const [msg, setMsg] = useState('');
  const [guardandoCfg, setGuardandoCfg] = useState(false);
  // reporte: calcula al vuelo por rango. cuadre: cierra el mes y guarda lo pagado.
  const [pestana, setPestana] = useState('reporte');

  const cargar = useCallback(async () => {
    try {
      const [v, c, r] = await Promise.all([
        api.get('/api/vendedores'),
        api.get('/api/vendedores/config'),
        api.get(`/api/vendedores/reporte?desde=${desde}&hasta=${hasta}`),
      ]);
      setVendedores(v.data);
      setConfig(c.data);
      setReporte(r.data);
    } catch (e) { setMsg('No se pudo cargar la información'); }
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirModal = (v) => {
    setEditando(v || null);
    setForm(v ? { nombre:v.nombre, documento:v.documento||'', telefono:v.telefono||'', email:v.email||'' }
              : { nombre:'', documento:'', telefono:'', email:'' });
    setMsg('');
    setModal(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) return setMsg('El nombre es requerido');
    try {
      if (editando) await api.put(`/api/vendedores/${editando.id}`, form);
      else await api.post('/api/vendedores', form);
      setModal(false); cargar();
    } catch (e) { setMsg(e.response?.data?.error || 'Error al guardar'); }
  };

  const desactivar = async (v) => {
    if (!window.confirm(`¿Desactivar a ${v.nombre}? No se borra: su historial de comisiones se conserva.`)) return;
    try { await api.delete(`/api/vendedores/${v.id}`); cargar(); }
    catch (e) { setMsg('Error al desactivar'); }
  };

  const guardarConfig = async () => {
    const suma = Number(config.pct_empresa) + Number(config.pct_equipo) + Number(config.pct_vendedor);
    if (Math.round(suma*100)/100 !== 100) return setMsg(`Los tres porcentajes deben sumar 100 (ahora suman ${suma})`);
    setGuardandoCfg(true); setMsg('');
    try {
      await api.put('/api/vendedores/config', config);
      setMsg('✓ Porcentajes guardados'); cargar();
    } catch (e) { setMsg(e.response?.data?.error || 'Error al guardar'); }
    finally { setGuardandoCfg(false); }
  };

  const t = reporte?.totales;

  return (
    <Layout>
      <div style={{ padding:'22px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <h1 style={{ fontSize:'21px', fontWeight:'700', color:'#fff' }}>👔 Vendedores y comisiones</h1>
            <p style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)', marginTop:'3px' }}>
              El reparto se calcula sobre la <b>ganancia</b> de cada venta, no sobre el total vendido.
            </p>
          </div>
          {esAdmin && <button style={{...s.btn,...s.btnP}} onClick={()=>abrirModal(null)}>+ Nuevo vendedor</button>}
        </div>

        {msg && <div style={{ background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.25)', color:'#93C5FD', padding:'11px 14px', borderRadius:'8px', marginBottom:'16px', fontSize:'13px' }}>{msg}</div>}

        {/* Dos formas de mirar lo mismo: el reporte calcula al vuelo por rango de fechas;
            el cuadre cierra el mes y deja constancia de lo que se pagó. */}
        <div style={{ display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap' }}>
          {[['reporte','📊 Reporte'], ['cuadre','🧾 Cuadre mensual']].map(([k, t]) => (
            <button key={k} onClick={() => setPestana(k)}
              style={{ padding:'8px 16px', borderRadius:'9px', fontSize:'13px', cursor:'pointer',
                background: pestana===k ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)',
                border: pestana===k ? '1px solid rgba(99,102,241,0.5)' : '1px solid rgba(255,255,255,0.1)',
                color: pestana===k ? '#A5B4FC' : 'rgba(255,255,255,0.55)',
                fontWeight: pestana===k ? '600' : '400' }}>{t}</button>
          ))}
        </div>

        {pestana === 'cuadre' && <CuadreMensual esAdmin={esAdmin} />}

        {pestana === 'reporte' && <>

        {/* Porcentajes de reparto */}
        <div style={s.card}>
          <h3 style={s.h3}>Reparto de la ganancia</h3>
          <div style={{ display:'flex', gap:'14px', flexWrap:'wrap', alignItems:'flex-end' }}>
            {[['pct_empresa','Empresa','#3B82F6'],['pct_equipo','Equipo (se divide entre todos)','#8B5CF6'],['pct_vendedor','Quien hizo la venta','#22C55E']].map(([k,label,color])=>(
              <div key={k} style={{ flex:'1 1 190px' }}>
                <label style={s.label}>{label}</label>
                <div style={{ position:'relative' }}>
                  <input style={{...s.input, color, fontWeight:'700', paddingRight:'28px'}} type="number" step="0.01" min="0" max="100"
                    disabled={!esAdmin}
                    value={config[k]} onChange={e=>setConfig({...config, [k]:e.target.value})}/>
                  <span style={{ position:'absolute', right:'11px', top:'11px', color:'rgba(255,255,255,0.35)', fontSize:'13px' }}>%</span>
                </div>
              </div>
            ))}
            {esAdmin && (
              <button style={{...s.btn,...s.btnP, height:'40px'}} onClick={guardarConfig} disabled={guardandoCfg}>
                {guardandoCfg ? 'Guardando…' : 'Guardar'}
              </button>
            )}
          </div>
          <div style={{ marginTop:'10px', fontSize:'12px', color:'rgba(255,255,255,0.35)' }}>
            Suma actual: <b style={{ color: Math.round((Number(config.pct_empresa)+Number(config.pct_equipo)+Number(config.pct_vendedor))*100)/100===100 ? '#22C55E':'#EF4444' }}>
              {Math.round((Number(config.pct_empresa)+Number(config.pct_equipo)+Number(config.pct_vendedor))*100)/100}%
            </b> — debe ser 100%
          </div>
        </div>

        {/* Periodo */}
        <div style={{ ...s.card, display:'flex', gap:'12px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div><label style={s.label}>Desde</label><input style={s.input} type="date" value={desde} onChange={e=>setDesde(e.target.value)}/></div>
          <div><label style={s.label}>Hasta</label><input style={s.input} type="date" value={hasta} onChange={e=>setHasta(e.target.value)}/></div>
        </div>

        {/* Totales del periodo */}
        {t && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(165px,1fr))', gap:'12px', marginBottom:'18px' }}>
            {[['Ventas', t.ventas, '#94A3B8', false],
              ['Vendido', t.vendido, '#fff', true],
              ['Ganancia', t.ganancia, '#22C55E', true],
              [`Empresa (${config.pct_empresa}%)`, t.empresa, '#3B82F6', true],
              [`Equipo (${config.pct_equipo}%)`, t.equipo, '#8B5CF6', true],
              [`Vendedores (${config.pct_vendedor}%)`, t.vendedores, '#F59E0B', true],
            ].map(([label,val,color,money])=>(
              <div key={label} style={{ ...s.card, marginBottom:0, padding:'14px 16px' }}>
                <div style={{ fontSize:'11.5px', color:'rgba(255,255,255,0.45)', marginBottom:'5px' }}>{label}</div>
                <div style={{ fontSize:'18px', fontWeight:'700', color }}>{money ? fmt(val) : val}</div>
              </div>
            ))}
          </div>
        )}

        {/* Detalle por vendedor */}
        <div style={s.card}>
          <h3 style={s.h3}>Qué le corresponde a cada uno</h3>
          {reporte && (
            <p style={{ fontSize:'12px', color:'rgba(255,255,255,0.4)', marginBottom:'12px' }}>
              La parte de "equipo" ({fmt(t?.equipo)}) se divide entre los {reporte.vendedores_activos} vendedores
              activos: <b style={{color:'#8B5CF6'}}>{fmt(reporte.equipo_por_vendedor)}</b> para cada uno.
            </p>
          )}
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:'720px' }}>
              <thead>
                <tr>
                  <th style={s.th}>Vendedor</th>
                  <th style={s.th}>Ventas</th>
                  <th style={s.th}>Vendido</th>
                  <th style={s.th}>Ganancia generada</th>
                  <th style={s.th}>Comisión propia</th>
                  <th style={s.th}>Parte del equipo</th>
                  <th style={s.th}>Total a pagar</th>
                  {esAdmin && <th style={s.th}>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {!reporte?.detalle?.length ? (
                  <tr><td colSpan="8" style={{...s.td, textAlign:'center', padding:'34px', color:'rgba(255,255,255,0.3)'}}>
                    Aún no hay vendedores registrados.
                  </td></tr>
                ) : reporte.detalle.map(v=>{
                  const info = vendedores.find(x=>x.id===v.id);
                  return (
                    <tr key={v.id} style={{ opacity: info && !info.activo ? 0.45 : 1 }}>
                      <td style={{...s.td, fontWeight:'600'}}>
                        {v.nombre}
                        {info && !info.activo && <span style={{ fontSize:'10.5px', color:'#EF4444', marginLeft:'7px' }}>inactivo</span>}
                      </td>
                      <td style={s.td}>{v.ventas}</td>
                      <td style={s.td}>{fmt(v.vendido)}</td>
                      <td style={{...s.td, color:'#22C55E'}}>{fmt(v.ganancia_generada)}</td>
                      <td style={{...s.td, color:'#F59E0B'}}>{fmt(v.comision_propia)}</td>
                      <td style={{...s.td, color:'#8B5CF6'}}>{fmt(v.parte_equipo)}</td>
                      <td style={{...s.td, fontWeight:'700', color:'#fff'}}>{fmt(v.total_a_pagar)}</td>
                      {esAdmin && (
                        <td style={s.td}>
                          <div style={{ display:'flex', gap:'6px' }}>
                            <button style={{...s.btn,...s.btnW,padding:'5px 10px'}} onClick={()=>abrirModal(info||v)}>✏️</button>
                            {info?.activo && <button style={{...s.btn,...s.btnR,padding:'5px 10px'}} onClick={()=>desactivar(info)}>🚫</button>}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        </>}

        {/* Modal alta/edición */}
        {modal && (
          <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:500,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px' }}>
            <div style={{ background:'#0F1629',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'14px',padding:'26px',width:'100%',maxWidth:'440px' }}>
              <h2 style={{ fontSize:'16px',fontWeight:'700',marginBottom:'16px',color:'#fff' }}>
                {editando ? 'Editar vendedor' : 'Nuevo vendedor'}
              </h2>
              {[['nombre','Nombre *'],['documento','Documento'],['telefono','Teléfono'],['email','Correo']].map(([k,label])=>(
                <div key={k} style={{ marginBottom:'12px' }}>
                  <label style={s.label}>{label}</label>
                  <input style={s.input} value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})}/>
                </div>
              ))}
              <div style={{ display:'flex', gap:'10px', marginTop:'18px', justifyContent:'flex-end' }}>
                <button style={{...s.btn,...s.btnW}} onClick={()=>setModal(false)}>Cancelar</button>
                <button style={{...s.btn,...s.btnP}} onClick={guardar}>Guardar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

const s = {
  card:{ background:'#0F1629', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'18px', marginBottom:'18px' },
  h3:{ fontSize:'14px', fontWeight:'700', color:'#fff', marginBottom:'13px' },
  label:{ display:'block', fontSize:'11.5px', fontWeight:'600', color:'rgba(255,255,255,0.5)', marginBottom:'5px' },
  input:{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'10px 12px', color:'#fff', fontSize:'13.5px', outline:'none', fontFamily:'inherit' },
  th:{ textAlign:'left', padding:'10px 12px', fontSize:'11px', fontWeight:'700', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', letterSpacing:'0.5px', borderBottom:'1px solid rgba(255,255,255,0.07)', whiteSpace:'nowrap' },
  td:{ padding:'11px 12px', fontSize:'13px', color:'rgba(255,255,255,0.82)', borderBottom:'1px solid rgba(255,255,255,0.04)' },
  btn:{ border:'none', borderRadius:'8px', padding:'9px 16px', fontSize:'13px', fontWeight:'600', cursor:'pointer', fontFamily:'inherit' },
  btnP:{ background:'linear-gradient(135deg,#2176D2,#14B8A6)', color:'#fff' },
  btnW:{ background:'rgba(255,255,255,0.08)', color:'#fff' },
  btnR:{ background:'rgba(239,68,68,0.15)', color:'#FCA5A5' },
};
